<!--
  A student's prepaid lessons, read like credit left (Miles, 2026-10-23): green is what's still
  free to book, yellow is booked but not done yet and eats into the green from its end, and done
  lessons are the empty grey part. "6 of 10 lessons used · 4 left".
-->
<script lang="ts">
  import { inUnits, type LessonProgress } from "@split/core";
  import { fill, shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";

  let { p, wide = false }: { p: LessonProgress; wide?: boolean } = $props();

  const n = (m: number) => inUnits(m, p.unitMinutes);
  const used = $derived(p.doneMinutes + p.pendingMinutes);
  const word = $derived(
    p.unit === "lesson"
      ? p.prepaidMinutes === p.unitMinutes
        ? "lesson"
        : "lessons"
      : p.prepaidMinutes === 60
        ? "hour"
        : "hours",
  );
  /** The bar's parts come from packages/core (lessonBar); its full width is what's prepaid. */
  const scale = $derived(Math.max(p.prepaidMinutes, 1));
  const pct = (m: number) => `${(m / scale) * 100}%`;
  const label = $derived(
    `${n(used)} of ${n(p.prepaidMinutes)} ${word} used: ${n(p.doneMinutes)} done, ${n(p.pendingMinutes)} booked, ${p.leftMinutes < 0 ? `${n(-p.leftMinutes)} over` : `${n(p.leftMinutes)} left`}`,
  );
</script>

<div class="lp" class:wide data-testid="lesson-progress">
  <div class="top">
    <span class="used"><strong>{n(used)}</strong> {fill(TT.BAR_USED, { prepaid: n(p.prepaidMinutes), word })}</span>
    <span class="left" class:over={p.leftMinutes < 0} class:out={p.leftMinutes === 0}>
      {p.leftMinutes < 0 ? fill(TT.BAR_OVER, { n: n(-p.leftMinutes) }) : fill(TT.BAR_LEFT, { n: n(p.leftMinutes) })}
    </span>
  </div>
  <div class="track" class:over={p.leftMinutes < 0} role="img" aria-label={label}>
    <span class="seg free" style:width={pct(p.bar.freeMinutes)}></span>
    <span class="seg pend" style:width={pct(p.bar.bookedMinutes)}></span>
  </div>
  <div class="legend">
    {#if shows(TT.BAR_LEGEND_LEFT)}<span><i class="sw free"></i>{fill(TT.BAR_LEGEND_LEFT, { n: n(p.bar.freeMinutes) })}</span>{/if}
    {#if shows(TT.BAR_LEGEND_BOOKED)}<span><i class="sw pend"></i>{fill(TT.BAR_LEGEND_BOOKED, { n: n(p.pendingMinutes) })}</span>{/if}
    {#if shows(TT.BAR_LEGEND_DONE)}<span><i class="sw done"></i>{fill(TT.BAR_LEGEND_DONE, { n: n(p.doneMinutes) })}</span>{/if}
  </div>
</div>

<style>
  .lp {
    --free: var(--ok);
    --pend: var(--pending);
    --done: var(--wash);
    display: grid;
    gap: 5px;
    width: 230px;
    max-width: 100%;
    font-size: var(--t-sm);
    white-space: nowrap;
  }

  .lp.wide {
    width: 100%;
  }

  .top {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: baseline;
  }

  .used {
    color: var(--ink-2);
  }

  .used strong {
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }

  .left {
    color: var(--ink);
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }

  .left.out,
  .left.over {
    color: var(--owes);
  }

  .track {
    position: relative;
    display: flex;
    height: 10px;
    overflow: hidden;
    border-radius: 999px;
    background: var(--wash);
    box-shadow: inset 0 0 0 1px var(--rule);
  }

  .track.over {
    box-shadow: inset 0 0 0 1px var(--owes);
  }

  .seg {
    display: block;
    height: 100%;
  }

  .seg {
    transition: width 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .seg.free {
    background: var(--free);
  }

  .seg.pend {
    background: var(--pend);
  }

  .seg.free + .seg.pend {
    box-shadow: inset 1px 0 0 var(--surface);
  }

  @media (prefers-reduced-motion: reduce) {
    .seg {
      transition: none;
    }
  }

  .legend {
    display: flex;
    gap: 12px;
    color: var(--muted);
    font-size: var(--t-xs);
  }

  .legend span {
    display: inline-flex;
    gap: 5px;
    align-items: center;
  }

  .sw {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }

  .sw.free {
    background: var(--free);
  }

  .sw.done {
    background: var(--done);
    box-shadow: inset 0 0 0 1px var(--rule);
  }

  .sw.pend {
    background: var(--pend);
  }
</style>
